import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import {
  Upload, Image as ImageIcon, Mic, Sparkles, Loader2, Download, RefreshCw,
  Save, ChevronDown, ChevronUp, Trash2, X, AlertCircle, CheckCircle2, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DropZone = ({ accept, onFile, label, hint, file, preview, icon: Icon, testid }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    onFile(files[0]);
  };

  return (
    <div
      data-testid={testid}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed transition-all p-6 text-center
        ${dragOver ? 'border-white bg-white/10' : 'border-white/20 bg-white/[0.03] hover:border-white/40 hover:bg-white/5'}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {preview ? (
        <div className="flex flex-col items-center gap-3">
          {preview}
          <p className="text-xs text-neutral-400">Click to replace</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 text-neutral-300">
          <Icon size={32} />
          <p className="text-sm font-semibold text-white">{label}</p>
          <p className="text-xs text-neutral-500">{hint}</p>
        </div>
      )}
    </div>
  );
};

const StudioPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();

  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [audioDuration, setAudioDuration] = useState(null);
  const [script, setScript] = useState('');

  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');

  const [characters, setCharacters] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [charName, setCharName] = useState('');
  const [charDesc, setCharDesc] = useState('');
  const [savingChar, setSavingChar] = useState(false);

  // Auth guard
  useEffect(() => {
    if (!authLoading && !user) {
      login('/studio');
    }
  }, [authLoading, user, login]);

  const fetchCharacters = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/studio/characters`, { credentials: 'include' });
      if (r.ok) setCharacters(await r.json());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (user) fetchCharacters(); }, [user, fetchCharacters]);

  // ---------- Uploads ----------
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API_URL}/api/upload/image`, {
      method: 'POST',
      body: fd,
      credentials: 'include',
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    const data = await r.json();
    return data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
  };

  const handleImageFile = async (file) => {
    setError('');
    if (!file.type.startsWith('image/')) {
      setError('Please upload a JPG, PNG, or WEBP image.');
      return;
    }
    setImageFile(file);
    setUploadingImage(true);
    try {
      const url = await uploadFile(file);
      setImageUrl(url);
    } catch (e) {
      setError(`Image upload failed: ${e.message}`);
      setImageFile(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioFile = async (file) => {
    setError('');
    if (!file.type.startsWith('audio/')) {
      setError('Please upload an MP3, WAV, or M4A audio file.');
      return;
    }
    setAudioFile(file);
    // Read duration locally — no backend audio endpoint, store URL via blob preview only.
    const audio = new Audio(URL.createObjectURL(file));
    audio.addEventListener('loadedmetadata', () => setAudioDuration(audio.duration));
    setUploadingAudio(true);
    try {
      // We don't have a dedicated audio upload route. Use image route which accepts files —
      // the file content itself is stored regardless of extension on the server side. As a
      // safer alternative we just keep the local blob URL since audio is only used as a
      // *reference* sample by the placeholder pipeline.
      setAudioUrl(URL.createObjectURL(file));
    } catch (e) {
      setError(`Audio handling failed: ${e.message}`);
    } finally {
      setUploadingAudio(false);
    }
  };

  // ---------- Generate + poll ----------
  const startGenerate = async () => {
    setError('');
    if (!imageUrl) { setError('Please upload a character image.'); return; }
    if (!script.trim()) { setError('Please enter a script.'); return; }

    setGenerating(true);
    setJob(null);
    try {
      const r = await fetch(`${API_URL}/api/studio/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url: imageUrl, audio_url: audioUrl || null, script }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Generation failed to start');
      }
      const j = await r.json();
      setJob(j);
      pollJob(j.job_id);
    } catch (e) {
      setError(e.message);
      setGenerating(false);
    }
  };

  const pollJob = async (jobId) => {
    const start = Date.now();
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/api/studio/jobs/${jobId}`, { credentials: 'include' });
        if (!r.ok) throw new Error('Status check failed');
        const j = await r.json();
        setJob(j);
        if (j.status === 'complete') {
          setGenerating(false);
          return;
        }
        if (Date.now() - start > 600000) {
          setError('Processing timed out after 10 minutes. Please try again.');
          setGenerating(false);
          return;
        }
        if (j.status === 'failed') {
          setError(j.error || 'Generation failed.');
          setGenerating(false);
          return;
        }
        setTimeout(tick, 3000);
      } catch (e) {
        setError(e.message);
        setGenerating(false);
      }
    };
    tick();
  };

  // ---------- Save / use character ----------
  const handleSaveCharacter = async () => {
    if (!charName.trim()) { setError('Character name is required.'); return; }
    if (!imageUrl) { setError('Upload an image before saving the character.'); return; }
    setSavingChar(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/studio/characters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          character_name: charName.trim(),
          description: charDesc.trim(),
          image_url: imageUrl,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      setCharName('');
      setCharDesc('');
      setSaveOpen(false);
      fetchCharacters();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingChar(false);
    }
  };

  const applyCharacter = (c) => {
    setImageUrl(c.image_url);
    setImageFile({ name: c.character_name });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteCharacter = async (id) => {
    if (!window.confirm('Delete this character?')) return;
    try {
      await fetch(`${API_URL}/api/studio/characters/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchCharacters();
    } catch (e) { /* silent */ }
  };

  const formatDuration = (s) => {
    if (!s || !isFinite(s)) return '';
    const m = Math.floor(s / 60), r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c]">
        <Loader2 className="w-10 h-10 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-4">
              <Sparkles size={14} /> Private Studio
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}>
              Animate Your Character
            </h1>
            <p className="text-neutral-400 max-w-xl mx-auto">
              Upload a character image, a voice sample, and a script. We'll bring it to life.
            </p>
          </div>

          {/* 1. Image */}
          <section className="mb-6">
            <h2 className="text-lg font-bold text-white mb-2">1. Upload your character image</h2>
            <DropZone
              testid="image-dropzone"
              accept="image/jpeg,image/png,image/webp"
              onFile={handleImageFile}
              label="Drag & drop or click"
              hint="JPG, PNG, WEBP"
              icon={ImageIcon}
              file={imageFile}
              preview={imageUrl && (
                <img src={imageUrl} alt="character preview" className="max-h-40 rounded-xl border border-white/10" />
              )}
            />
            {uploadingImage && <p className="text-xs text-neutral-400 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading…</p>}
          </section>

          {/* 2. Audio */}
          <section className="mb-6">
            <h2 className="text-lg font-bold text-white mb-2">2. Upload voice sample <span className="text-xs font-normal text-neutral-500">(optional)</span></h2>
            <DropZone
              testid="audio-dropzone"
              accept="audio/mpeg,audio/wav,audio/x-m4a,audio/mp4,audio/*"
              onFile={handleAudioFile}
              label="Drag & drop or click"
              hint="MP3, WAV, M4A"
              icon={Mic}
              file={audioFile}
              preview={audioFile && (
                <div className="text-center">
                  <Mic size={24} className="mx-auto text-neutral-300 mb-1" />
                  <p className="text-sm font-medium text-white">{audioFile.name}</p>
                  {audioDuration != null && <p className="text-xs text-neutral-400">{formatDuration(audioDuration)}</p>}
                </div>
              )}
            />
            {uploadingAudio && <p className="text-xs text-neutral-400 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Processing…</p>}
            <p className="mt-2 text-xs text-neutral-500 flex items-start gap-1"><Info size={12} className="mt-0.5 flex-shrink-0" />
              Voice cloning will use Emergent's native voice synthesis once enabled. Until then your sample is stored as a reference.
            </p>
          </section>

          {/* 3. Script */}
          <section className="mb-6">
            <h2 className="text-lg font-bold text-white mb-2">3. Script</h2>
            <textarea
              data-testid="script-textarea"
              value={script}
              onChange={(e) => setScript(e.target.value.slice(0, 500))}
              placeholder="What should your character say?"
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-white/20 resize-y"
            />
            <div className="flex justify-end mt-1 text-xs text-neutral-500">{script.length} / 500</div>
          </section>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-2 text-sm text-red-400" data-testid="studio-error">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* 4. Generate */}
          <button
            data-testid="generate-btn"
            onClick={startGenerate}
            disabled={!imageUrl || !script.trim() || generating || uploadingImage}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-white text-black font-semibold rounded-xl shadow-lg shadow-white/10 hover:shadow-white/20 hover:-translate-y-0.5 hover:bg-neutral-200 transition-all disabled:opacity-40 disabled:hover:translate-y-0"
          >
            {generating
              ? <><Loader2 size={18} className="animate-spin" /> Generating…</>
              : <><Sparkles size={18} /> Generate Video</>}
          </button>

          {/* Progress / Output */}
          {job && (
            <section className="mt-8" data-testid="job-output">
              {job.status === 'processing' && (
                <div className="p-6 bg-white/[0.04] border border-white/10 rounded-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <Loader2 className="text-white animate-spin" size={20} />
                    <p className="text-white font-medium">{job.current_step}…</p>
                  </div>
                  <div className="space-y-2">
                    {job.steps.map((step, i) => (
                      <div key={step} className="flex items-center gap-2 text-sm">
                        {i < job.step_index
                          ? <CheckCircle2 size={16} className="text-green-500" />
                          : i === job.step_index
                            ? <Loader2 size={16} className="text-white animate-spin" />
                            : <div className="w-4 h-4 rounded-full border border-white/20" />}
                        <span className={i <= job.step_index ? 'text-neutral-200' : 'text-neutral-500'}>{step}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-white transition-all" style={{ width: `${job.progress}%` }} />
                  </div>
                </div>
              )}

              {job.status === 'complete' && (
                <div className="p-6 bg-white/[0.04] border border-white/10 rounded-2xl">
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    <CheckCircle2 className="text-green-500" size={20} /> Video Ready
                  </h3>
                  <div className="aspect-video bg-black rounded-xl overflow-hidden mb-3">
                    <video
                      src={job.output_url?.startsWith('http') ? job.output_url : `${API_URL}${job.output_url}`}
                      controls
                      autoPlay
                      loop
                      className="w-full h-full"
                      data-testid="output-video"
                    />
                  </div>
                  {job.limitation_notice && (
                    <div className="mb-3 p-3 bg-white/5 border border-white/10 rounded-xl text-xs text-neutral-300 flex items-start gap-2">
                      <Info size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{job.limitation_notice}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={job.output_url?.startsWith('http') ? job.output_url : `${API_URL}${job.output_url}`}
                      download={`character-${job.job_id}.mp4`}
                      data-testid="download-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-medium rounded-lg hover:bg-neutral-200"
                    >
                      <Download size={16} /> Download Video
                    </a>
                    <button
                      onClick={startGenerate}
                      data-testid="regenerate-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/10"
                    >
                      <RefreshCw size={16} /> Regenerate
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* 5. Save as Character */}
          <section className="mt-10 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
            <button
              onClick={() => setSaveOpen(!saveOpen)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              data-testid="toggle-save"
            >
              <span className="flex items-center gap-2 text-white font-semibold">
                <Save size={18} /> Save this as a reusable character
              </span>
              {saveOpen ? <ChevronUp size={18} className="text-neutral-400" /> : <ChevronDown size={18} className="text-neutral-400" />}
            </button>
            {saveOpen && (
              <div className="px-5 pb-5 border-t border-white/10 pt-4 space-y-3">
                <input
                  data-testid="char-name-input"
                  type="text"
                  value={charName}
                  onChange={(e) => setCharName(e.target.value)}
                  placeholder="Character name"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                />
                <textarea
                  value={charDesc}
                  onChange={(e) => setCharDesc(e.target.value)}
                  placeholder="Description (optional)"
                  rows={2}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/20 resize-y"
                />
                <button
                  onClick={handleSaveCharacter}
                  disabled={savingChar || !charName.trim() || !imageUrl}
                  data-testid="save-character-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-medium rounded-lg hover:bg-neutral-200 disabled:opacity-50"
                >
                  {savingChar ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Character
                </button>
              </div>
            )}
          </section>

          {/* Saved characters grid */}
          {characters.length > 0 && (
            <section className="mt-10" data-testid="saved-characters">
              <h2 className="text-lg font-bold text-white mb-4">Your saved characters</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {characters.map((c) => (
                  <div key={c.character_id} className="group relative bg-white/[0.04] border border-white/10 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-white/30 transition-all">
                    <button
                      onClick={() => deleteCharacter(c.character_id)}
                      className="absolute top-2 right-2 z-10 p-1.5 bg-black/70 rounded-full text-neutral-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                    <div className="aspect-square bg-white/5">
                      <img src={c.image_url} alt={c.character_name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-white truncate">{c.character_name}</p>
                      {c.description && <p className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{c.description}</p>}
                      <button
                        onClick={() => applyCharacter(c)}
                        data-testid={`use-character-${c.character_id}`}
                        className="mt-2 w-full text-xs font-medium px-2 py-1.5 bg-white/10 text-neutral-200 rounded hover:bg-white hover:text-black transition-colors"
                      >
                        Use this character
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StudioPage;
